// ============================================================================
// afipIva.js — Cálculo de IVA por alícuota para facturación electrónica (AFIP/ARCA)
// ----------------------------------------------------------------------------
// Fuente única de verdad para armar el desglose de IVA que se envía a la edge
// function `arca-facturar`. Garantiza que el comprobante autorizado por AFIP
// (CAE) tenga EXACTAMENTE la misma discriminación de IVA, alícuota por alícuota,
// que el ticket que ve el cliente.
//
// Premisa: los precios del POS son FINALES (IVA incluido). Por eso, por defecto,
// el neto se obtiene descontando el IVA del precio (price / (1 + tasa)), no
// sumándolo encima.
//
// Reglas:
//  - Cada ítem aporta su propia alícuota (snapshot congelado en sale_items, o el
//    rate actual del producto si la venta es vieja y no tiene snapshot).
//  - Los productos EXENTOS (0%) van a ImpOpEx (operaciones exentas), NO como un
//    bloque AlicIva al 0%.
//  - Para no duplicar importes, se excluyen las filas "component" de combos: se
//    factura por la fila visible (parent) o por el producto suelto.
//  - El IVA de cada grupo se calcula a nivel grupo (Importe = round(BaseImp * tasa))
//    para que cada bloque <AlicIva> pase la validación de AFIP sin desfasajes de
//    redondeo.
// ============================================================================

// Mapeo porcentaje -> Id de alícuota AFIP (FEParamGetTiposIva)
export const afipIvaIdFromPercent = (pct) => {
  const p = Number(pct);
  if (p === 0) return 3; // 0%
  if (p === 10.5) return 4; // 10,5%
  if (p === 21) return 5; // 21%
  if (p === 27) return 6; // 27%
  if (p === 5) return 8; // 5%
  if (p === 2.5) return 9; // 2,5%
  return 5; // fallback conservador: 21%
};

// Mapeo inverso Id de alícuota AFIP -> porcentaje (para mostrar el desglose).
export const afipPercentFromIvaId = (id) => {
  switch (Number(id)) {
    case 3: return 0;
    case 4: return 10.5;
    case 5: return 21;
    case 6: return 27;
    case 8: return 5;
    case 9: return 2.5;
    default: return 21;
  }
};

export const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

// Alícuota de un sale_item, en orden de preferencia:
//   1) snapshot congelado en la fila (sale_items.iva_percentage)
//   2) rate actual del producto vía join (products.iva_percentage)
//   3) variantes de nombre por compatibilidad
//   4) fallback (21 por defecto)
export const getItemIvaPercent = (item, fallback = 21) => {
  const candidates = [
    item?.iva_percentage,
    item?.products?.iva_percentage,
    item?.iva_percent,
    item?.iva_rate,
    item?.vat_percent,
    item?.tax_percent,
  ];
  for (const c of candidates) {
    if (c === null || c === undefined || c === '') continue;
    const v = Number(c);
    if (!Number.isNaN(v) && v >= 0) return v;
  }
  return fallback;
};

// Importe de una línea (preferimos el subtotal persistido; si no, qty * unit).
const lineGross = (it) => {
  let total = Number(it?.subtotal);
  if (!isFinite(total) || total <= 0) {
    const qty = Number(it?.quantity ?? it?.qty ?? 1);
    const unit = Number(it?.unit_price ?? it?.price ?? it?.unitPrice ?? 0);
    total = qty * unit;
  }
  return isFinite(total) ? total : 0;
};

// Filas facturables: excluye los componentes de combos (su revenue ya está
// representado por la fila parent). Deja parents y productos sueltos.
export const selectBillableItems = (items) =>
  (Array.isArray(items) ? items : []).filter((it) => it?.combo_role !== 'component');

// Construye { iva, totals, impTotal } para AFIP a partir de los sale_items.
// pricesIncludeIva = true  -> precio final, se descuenta el IVA.
// pricesIncludeIva = false -> precio neto, se suma el IVA.
// saleTotal (opcional) -> total REAL de la venta (con descuento/recargo de cabecera).
//   Si se pasa y difiere de la suma de las líneas, se prorratea cada alícuota para
//   que ImpNeto + ImpIVA + ImpOpEx == saleTotal. Los descuentos/recargos a nivel
//   venta no se bajan a las líneas, así que sin esto se facturaría el precio de lista.
export const buildAfipIvaBreakdown = (rawItems, options = {}) => {
  const { pricesIncludeIva = true, defaultPercent = 21, saleTotal = null } = options;
  const items = selectBillableItems(rawItems);

  const grossByPercent = new Map(); // pct(>0) -> bruto acumulado
  let exentoGross = 0;

  for (const it of items) {
    const gross = lineGross(it);
    if (gross <= 0) continue;
    const pct = getItemIvaPercent(it, defaultPercent);
    if (pct === 0) {
      exentoGross = round2(exentoGross + gross);
      continue;
    }
    grossByPercent.set(pct, round2((grossByPercent.get(pct) || 0) + gross));
  }

  // Prorrateo al total real de la venta (descuento/recargo de cabecera).
  const sumGross = round2(
    exentoGross + Array.from(grossByPercent.values()).reduce((a, b) => a + b, 0),
  );
  const target = Number(saleTotal);
  if (isFinite(target) && target > 0 && sumGross > 0 && Math.abs(target - sumGross) >= 0.01) {
    const factor = target / sumGross;
    exentoGross = round2(exentoGross * factor);
    for (const [pct, gross] of grossByPercent.entries()) {
      grossByPercent.set(pct, round2(gross * factor));
    }
  }

  // Ajuste del residuo de redondeo del prorrateo: garantiza exento + Σ(grupos) ==
  // target exacto, para que ImpNeto + ImpIVA + ImpOpEx == saleTotal (lo exige AFIP).
  // El residuo se empuja al grupo gravado de mayor base (o a exento si no hay). Bug P2.
  if (isFinite(target) && target > 0) {
    const recomputed = round2(
      exentoGross + Array.from(grossByPercent.values()).reduce((a, b) => a + b, 0),
    );
    const residual = round2(target - recomputed);
    if (residual !== 0) {
      const entries = Array.from(grossByPercent.entries()).sort((a, b) => b[1] - a[1]);
      if (entries.length > 0) {
        grossByPercent.set(entries[0][0], round2(entries[0][1] + residual));
      } else {
        exentoGross = round2(exentoGross + residual);
      }
    }
  }

  const iva = [];
  let impNeto = 0;
  let impIVA = 0;

  for (const [pct, gross] of grossByPercent.entries()) {
    const rate = pct / 100;
    const base = pricesIncludeIva ? round2(gross / (1 + rate)) : round2(gross);
    // IVA incluido: tax = gross - base garantiza base + tax == gross (antes era
    // round2(base*rate) y daba base+tax != gross, p.ej. venta $100 -> total 99.99). Bug P2.
    const tax = pricesIncludeIva ? round2(gross - base) : round2(base * rate);
    impNeto = round2(impNeto + base);
    impIVA = round2(impIVA + tax);
    iva.push({ Id: afipIvaIdFromPercent(pct), BaseImp: base, Importe: tax });
  }

  const impOpEx = round2(exentoGross);

  const totals = {
    ImpNeto: impNeto,
    ImpIVA: impIVA,
    ImpOpEx: impOpEx,
    ImpTotConc: 0,
    ImpTrib: 0,
  };

  return { iva, totals, impTotal: round2(impNeto + impIVA + impOpEx) };
};
